/**
 * Thin client over the No. 4 High Terrace Supabase Edge Functions.
 *
 * All requests include:
 *   - Authorization: Bearer <anonKey>     (Supabase anon key — safe to expose)
 *   - apikey: <anonKey>                   (required by Supabase gateway)
 *   - Content-Type: application/json
 *
 * No service-role keys, no Stripe secrets, no provider secrets are
 * ever sent from the browser. All sensitive work happens server-side
 * inside the edge functions.
 */

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

export interface BlockedRange {
  start: string; // YYYY-MM-DD inclusive
  end: string; // YYYY-MM-DD inclusive
  reason?: string;
}

export interface AvailabilityResponse {
  blockedRanges: BlockedRange[];
  minNightsStandard: number;
  minNightsSummer: number;
  summerStart: string; // YYYY-MM-DD inclusive (annual recurrence — month/day used)
  summerEnd: string; // YYYY-MM-DD inclusive
  baseRatePence: number;
  couplesDiscountPct: number;
  perPersonReductionPct: number;
  depositPct: number;
  maxAdults: number;
  maxOccupancy: number;
}

export interface PriceBreakdown {
  numNights: number;
  nightlyRatePence: number;
  totalPricePence: number;
  depositPence: number;
  balancePence: number;
  balanceDueDate: string; // YYYY-MM-DD
  discountType: 'couples' | 'taper' | 'none';
  discountPct: number;
}

export interface CalculatePriceParams {
  check_in: string;
  check_out: string;
  num_adults: number;
  num_children: number;
}

export interface CreateBookingRequest {
  type: 'booking' | 'enquiry';
  check_in: string;
  check_out: string;
  num_adults: number;
  num_children: number;
  children_ages: string[];
  has_pets: boolean;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  notes: string;
  gdpr_consent: boolean;
}

export interface CreateBookingResponse {
  bookingId: string;
  reference: string;
  stripeClientSecret?: string; // undefined for enquiries
  priceBreakdown: PriceBreakdown;
}

/* -------------------------------------------------------------------------- */
/*  Error type                                                                */
/* -------------------------------------------------------------------------- */

export class APIError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly details: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/* -------------------------------------------------------------------------- */
/*  Client                                                                    */
/* -------------------------------------------------------------------------- */

export class BookingAPI {
  private readonly base: string;

  constructor(
    supabaseUrl: string,
    private readonly anonKey: string,
  ) {
    if (!supabaseUrl) throw new Error('BookingAPI: supabaseUrl is required');
    if (!anonKey) throw new Error('BookingAPI: anonKey is required');
    this.base = supabaseUrl.replace(/\/+$/, '') + '/functions/v1';
  }

  async getAvailability(): Promise<AvailabilityResponse> {
    return this.request<AvailabilityResponse>('GET', '/get-availability');
  }

  async calculatePrice(params: CalculatePriceParams): Promise<PriceBreakdown> {
    // Retry on 5xx once — calculate-price occasionally cold-starts and hits
    // a transient Supabase JWT-clock skew that resolves on retry. Do NOT
    // retry 4xx (real validation errors we want the caller to see).
    return this.retryOn5xx(() =>
      this.request<PriceBreakdown>('POST', '/calculate-price', params),
    );
  }

  async getAvailabilityWithRetry(): Promise<AvailabilityResponse> {
    return this.retryOn5xx(() => this.getAvailability());
  }

  async createBooking(data: CreateBookingRequest): Promise<CreateBookingResponse> {
    // create-booking is money-related — retry on 5xx once too, but only if
    // the failure looks transient. Never retry a 4xx (validation) or 409 (dates).
    return this.retryOn5xx(() =>
      this.request<CreateBookingResponse>('POST', '/create-booking', data),
    );
  }

  private async retryOn5xx<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      if (e instanceof APIError && e.status >= 500 && e.status < 600) {
        // Wait a beat, then try once more. If it fails again, propagate.
        await new Promise((r) => setTimeout(r, 400));
        return fn();
      }
      throw e;
    }
  }

  /* ----------------------------------------------------------------------- */

  private async request<T>(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = this.base + path;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${this.anonKey}`,
      apikey: this.anonKey,
      Accept: 'application/json',
    };
    const init: RequestInit = { method, headers };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }

    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Network error';
      throw new APIError(`Network error: ${msg}`, 0, 'network_error');
    }

    const text = await response.text();
    let parsed: unknown = undefined;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        // non-JSON response body
      }
    }

    if (!response.ok) {
      const payload = (parsed ?? {}) as Record<string, unknown>;
      const message =
        (typeof payload.error === 'string' && payload.error) ||
        (typeof payload.message === 'string' && payload.message) ||
        `Request failed (${response.status})`;
      const code = typeof payload.code === 'string' ? payload.code : undefined;
      throw new APIError(message, response.status, code, parsed);
    }

    if (parsed === null || parsed === undefined) {
      throw new APIError('Empty response from server', 0, 'empty_body');
    }

    return parsed as T;
  }
}
